// VentaFormXL - Sprint 44 Fase 3 Vista 4.
// Contenedor XL del formulario de venta. Header con back + titulo + badge
// estado + total + acciones dinamicas (SendButtons legacy reusado, PDF,
// Emitir, Anular, FEInlineActionsXL, Guardar). Tabs XLChip. Body con
// Datos / Productos (LineasPanelXL) / Auditoria.
import { useEffect, useMemo } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useVentaFormXL from './hooks/useVentaFormXL';
import * as svc from '../ventas/services/ventasService';
import XLBadge from '../../components/xl/XLBadge';
import XLChip from '../../components/xl/XLChip';
import XLSkeleton from '../../components/xl/XLSkeleton';
import LineasPanelXL from '../../components/comercial/LineasPanelXL';
import FEInlineActionsXL from '../facturacionElectronica/FEInlineActionsXL';
import SendButtons from '../../components/shared/SendButtons';
import openProtectedFile from '../../api/openProtectedFile';
import { fmtMoney } from '../../utils/money';
import DatosVentaTabXL from './tabs/DatosVentaTabXL';
import AuditoriaVentaTabXL from './tabs/AuditoriaVentaTabXL';

const CSS_ID = 'xl-venta-form-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-hdr { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-hdr__left { display: flex; align-items: center; gap: 14px; min-width: 0 }
.xl-hdr__back {
  font: 500 13px/1 var(--xl-font-sans);
  color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 8px; border-radius: var(--xl-radius-md);
}
.xl-hdr__back:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-hdr__back:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-hdr__title { margin: 0; font-size: 20px; font-weight: 600; line-height: 1.2 }
.xl-hdr__sub { margin: 2px 0 0; font-size: 12px; color: var(--xl-color-text-secondary) }
.xl-hdr__sub b { color: var(--xl-color-text); font-family: var(--xl-font-mono) }
.xl-hdr__actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap }

.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
}
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn--success { background: var(--xl-color-success); color: var(--xl-color-text-inverse) }
.xl-btn--success:hover { filter: brightness(1.08) }
.xl-btn--ghost {
  background: transparent; color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }
.xl-btn--danger {
  background: transparent; color: var(--xl-color-danger);
  border-color: rgba(220,38,38,0.35);
}
.xl-btn--danger:hover { background: rgba(220,38,38,0.08) }

.xl-banner { padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px }
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-card {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  overflow: hidden;
}
.xl-tabs {
  display: flex; gap: 6px; flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid var(--xl-color-border);
  background: var(--xl-color-bg);
}
.xl-body { padding: 20px }
.xl-loading { padding: 20px; display: flex; flex-direction: column; gap: 10px }
`;

const ESTADO_VARIANT = { borrador: 'neutral', emitida: 'success', anulada: 'danger' };
const ESTADO_LABEL   = { borrador: 'Borrador', emitida: 'Emitida',  anulada: 'Anulada'  };

const TABS = [
  { id: 'datos',     label: 'Datos' },
  { id: 'lineas',    label: 'Productos' },
  { id: 'auditoria', label: 'Auditoria' },
];

export default function VentaFormXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const fm = useVentaFormXL();

  const lineasApi = useMemo(() => ({
    listLineas: svc.listLineas,
    createLinea: svc.createLinea,
    updateLinea: svc.updateLinea,
    deleteLinea: svc.deleteLinea,
    updateDoc: (id, patch) => svc.updateVenta(id, patch),
    recalcDoc: svc.recalcVenta,
  }), []);

  if (!fm.loaded) {
    return (
      <div className="xl-page">
        <div className="xl-loading" aria-busy="true">
          <XLSkeleton variant="line" width="30%" height={18} />
          <XLSkeleton variant="rect" height={120} />
          <XLSkeleton variant="rect" height={220} />
        </div>
      </div>
    );
  }

  const isEditable = fm.form.estado === 'borrador' && !fm.isNew;
  const titulo = fm.isNew ? 'Nueva venta' : (fm.form.numero || 'Venta');
  const clienteNombre = fm.form.cliente && fm.form.cliente.nombre;
  const variant = ESTADO_VARIANT[fm.form.estado] || 'neutral';
  const estLabel = ESTADO_LABEL[fm.form.estado] || fm.form.estado;

  const onPdf = async () => {
    if (fm.isNew) return;
    try {
      await openProtectedFile('/ventas/' + fm.id + '/pdf', 'application/pdf');
    } catch (e) {
      alert('No se pudo abrir el PDF: ' + ((e && e.message) || 'error'));
    }
  };

  return (
    <div className="xl-page">
      <header className="xl-hdr">
        <div className="xl-hdr__left">
          <button type="button" className="xl-hdr__back" onClick={() => fm.navigate('/ventas')}>
            {'\u2190 Volver'}
          </button>
          <div>
            <h2 className="xl-hdr__title">{titulo}</h2>
            <p className="xl-hdr__sub">
              {clienteNombre || 'Sin cliente'}{' \u00b7 '}
              <XLBadge variant={variant} dot>{estLabel}</XLBadge>
              {' \u00b7 '}Total: <b>{fmtMoney(fm.form.total, fm.form.moneda)}</b>
            </p>
          </div>
        </div>
        <div className="xl-hdr__actions">
          {!fm.isNew && (
            <SendButtons
              endpointBase={'/ventas/' + fm.id}
              tipoLabel="venta"
              defaultEmail={(fm.form.cliente && fm.form.cliente.email) || ''}
              defaultTelefono={(fm.form.cliente && fm.form.cliente.telefono) || ''}
            />
          )}
          {!fm.isNew && (
            <button type="button" className="xl-btn xl-btn--ghost" onClick={onPdf}>
              PDF
            </button>
          )}
          {fm.form.estado === 'borrador' && !fm.isNew && (
            <button type="button" className="xl-btn xl-btn--success"
              onClick={fm.onEmitir} disabled={fm.emitting}>
              {fm.emitting ? 'Emitiendo\u2026' : 'Emitir venta'}
            </button>
          )}
          {fm.form.estado === 'emitida' && (
            <button type="button" className="xl-btn xl-btn--danger"
              onClick={fm.onAnular} disabled={fm.acting}>
              {fm.acting ? 'Procesando\u2026' : 'Anular'}
            </button>
          )}
          {fm.form.estado === 'emitida' && !fm.isNew && (
            <FEInlineActionsXL ventaId={fm.id} />
          )}
          {(isEditable || fm.isNew) && (
            <button type="button" className="xl-btn xl-btn--primary"
              onClick={fm.onSave} disabled={fm.saving}>
              {fm.saving ? 'Guardando\u2026' : (fm.isNew ? 'Crear borrador' : 'Guardar')}
            </button>
          )}
        </div>
      </header>

      {fm.msg && (
        <div
          className={'xl-banner ' + (fm.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={fm.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={fm.clearMsg}
        >
          {fm.msg.text}
        </div>
      )}

      <div className="xl-card">
        <div className="xl-tabs" role="tablist">
          {TABS.map((t) => (
            <XLChip key={t.id} onClick={() => fm.setTab(t.id)} selected={fm.tab === t.id}>
              {t.label}
            </XLChip>
          ))}
        </div>
        <div className="xl-body">
          {fm.tab === 'datos' && (
            <DatosVentaTabXL form={fm.form} set={fm.set} isEditable={isEditable || fm.isNew} />
          )}
          {fm.tab === 'lineas' && (
            <LineasPanelXL
              docId={fm.id}
              docType="venta"
              isNew={fm.isNew}
              isEditable={isEditable}
              moneda={fm.form.moneda}
              doc={fm.form}
              api={lineasApi}
              onChanged={fm.onRefresh}
            />
          )}
          {fm.tab === 'auditoria' && <AuditoriaVentaTabXL form={fm.form} />}
        </div>
      </div>
    </div>
  );
}
